"use client";

import { BellIcon, BellOffIcon, XIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../../cn";
import { Button } from "../button";
import { MessageList, type MessageListProps } from "./message-list";
import type { ChatMessage } from "./types";

// A message's thread in the 360 px side panel (spec M2-06 "thread in side panel"; design spec §2
// side panel): a 40 px header (title, the topic as a muted caption, follow toggle, close), the
// root message with a "n replies" divider under it and the replies in one scroller, and the
// caller's composer pinned at the bottom. Replies are one level deep, as in Work OS.

export type ThreadPanelProps = Omit<React.ComponentProps<"section">, "children"> &
  Pick<
    MessageListProps,
    | "currentUserId"
    | "lastReadId"
    | "now"
    | "locale"
    | "quickReactions"
    | "onReact"
    | "onPinChange"
    | "onEdit"
    | "onDelete"
    | "extraActions"
    | "menuItems"
    | "hasEarlier"
    | "loadingEarlier"
    | "onLoadEarlier"
    | "renderAuthor"
    | "highlightId"
  > & {
    root: ChatMessage;
    replies: ChatMessage[];
    /** "#ops › Weekly export". */
    context?: React.ReactNode;
    following?: boolean;
    onFollowingChange?: (following: boolean) => void;
    onClose?: () => void;
    /** Usually `<Composer placeholder="Reply…" />`. */
    composer?: React.ReactNode;
    /** Header extras before the follow toggle (e.g. the agents taking part, as chips). */
    headerExtra?: React.ReactNode;
    /** At the bottom of the thread, above the composer: e.g. "Riley is working…". */
    statusLine?: React.ReactNode;
  };

export function ThreadPanel({
  root,
  replies,
  context,
  following = false,
  onFollowingChange,
  onClose,
  composer,
  headerExtra,
  statusLine,
  className,
  "aria-label": ariaLabel = "Thread",
  ...listProps
}: ThreadPanelProps) {
  const {
    currentUserId,
    lastReadId,
    now,
    locale,
    quickReactions,
    onReact,
    onPinChange,
    onEdit,
    onDelete,
    extraActions,
    menuItems,
    hasEarlier,
    loadingEarlier,
    onLoadEarlier,
    renderAuthor,
    highlightId,
    ...props
  } = listProps;
  const count = replies.length;
  // The root's summary line would point at the panel it is already in.
  const { thread: _summary, ...rootShown } = root;
  return (
    <section
      data-slot="thread-panel"
      aria-label={ariaLabel}
      className={cn("flex h-full min-h-0 flex-col bg-card text-card-foreground", className)}
      {...props}
    >
      <header className="flex h-10 shrink-0 items-center gap-2 border-b border-border pr-2 pl-4">
        <h2 className="text-sm font-semibold text-foreground">Thread</h2>
        {context ? (
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{context}</span>
        ) : (
          <span className="flex-1" />
        )}
        {headerExtra}
        {onFollowingChange ? (
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={following}
            onClick={() => onFollowingChange(!following)}
            className="aria-pressed:text-primary-text"
          >
            {following ? (
              <BellIcon data-icon="inline-start" />
            ) : (
              <BellOffIcon data-icon="inline-start" />
            )}
            {following ? "Following" : "Follow"}
          </Button>
        ) : null}
        {onClose ? (
          <Button
            variant="ghost"
            size="icon-sm"
            className="pointer-coarse:size-11"
            aria-label="Close thread"
            onClick={onClose}
          >
            <XIcon aria-hidden={true} />
          </Button>
        ) : null}
      </header>
      <MessageList
        label="Thread messages"
        messages={[rootShown, ...replies]}
        dividers={[
          {
            afterId: root.id,
            label: count === 0 ? "No replies yet" : `${count} ${count === 1 ? "reply" : "replies"}`,
          },
        ]}
        empty={null}
        currentUserId={currentUserId}
        lastReadId={lastReadId}
        now={now}
        locale={locale}
        quickReactions={quickReactions}
        onReact={onReact}
        onPinChange={onPinChange}
        onEdit={onEdit}
        onDelete={onDelete}
        extraActions={extraActions}
        menuItems={menuItems}
        hasEarlier={hasEarlier}
        loadingEarlier={loadingEarlier}
        onLoadEarlier={onLoadEarlier}
        renderAuthor={renderAuthor}
        highlightId={highlightId}
      />
      {statusLine ? (
        <div data-slot="thread-panel-status" className="shrink-0 px-4 pb-1">
          {statusLine}
        </div>
      ) : null}
      {composer ? (
        <div
          data-slot="thread-panel-composer"
          className="shrink-0 border-t border-border-subtle p-3"
        >
          {composer}
        </div>
      ) : null}
    </section>
  );
}
